import React, { useState, useEffect } from 'react';
import {
  Activity,
  Search,
  Filter,
  Calendar,
  User,
  CheckCircle,
  XCircle,
  Clock,
  Download,
  AlertCircle,
  FileText,
  Image,
  Target,
  Mail,
  Users
} from 'lucide-react';
import { ActivityLog } from '../types';
import { PocketBaseDataService as DataService } from '../services/pocketbaseDataService';

interface ActivityLogsManagerProps {
  partnerId?: string;
  userRole?: 'global_admin' | 'partner_admin';
}

export const ActivityLogsManager: React.FC<ActivityLogsManagerProps> = ({ partnerId, userRole = 'global_admin' }) => {
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [logTypeFilter, setLogTypeFilter] = useState<string>('all');
  const [resultFilter, setResultFilter] = useState<string>('all');
  const [dateRange, setDateRange] = useState<'today' | 'week' | 'month' | 'all'>('all');
  const isPartnerView = userRole === 'partner_admin';

  useEffect(() => {
    loadLogs();
  }, [partnerId]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const { data, error } = await DataService.getActivityLogs(partnerId);
      if (error) throw new Error(error);
      if (data) {
        setLogs(data as ActivityLog[]);
      }
    } catch (error) {
      console.error('Failed to load activity logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredLogs = logs.filter(log => {
    const matchesSearch = 
      log.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.actor_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (log.target_name && log.target_name.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesLogType = logTypeFilter === 'all' || log.log_type === logTypeFilter;
    const matchesResult = resultFilter === 'all' || log.result === resultFilter;
    
    let matchesDate = true;
    if (dateRange !== 'all') {
      const logDate = new Date(log.created);
      const now = new Date();
      const dayMs = 24 * 60 * 60 * 1000;
      
      if (dateRange === 'today') {
        matchesDate = now.getTime() - logDate.getTime() < dayMs;
      } else if (dateRange === 'week') {
        matchesDate = now.getTime() - logDate.getTime() < 7 * dayMs;
      } else if (dateRange === 'month') {
        matchesDate = now.getTime() - logDate.getTime() < 30 * dayMs;
      }
    }
    
    return matchesSearch && matchesLogType && matchesResult && matchesDate;
  });

  const getLogTypeIcon = (logType: string) => {
    switch (logType) {
      case 'user_action': return <User size={16} />;
      case 'advertisement_action': return <Image size={16} />;
      case 'campaign_action': return <Target size={16} />;
      case 'promo_request_action': return <Mail size={16} />;
      case 'partner_action': return <Users size={16} />;
      default: return <Activity size={16} />;
    }
  };

  const getLogTypeColor = (logType: string) => {
    switch (logType) {
      case 'user_action': return 'bg-blue-100 text-blue-800';
      case 'advertisement_action': return 'bg-purple-100 text-purple-800';
      case 'campaign_action': return 'bg-green-100 text-green-800';
      case 'promo_request_action': return 'bg-orange-100 text-orange-800';
      case 'partner_action': return 'bg-pink-100 text-pink-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getResultIcon = (result: string) => {
    switch (result) {
      case 'success': return <CheckCircle size={16} className="text-green-600" />;
      case 'failure': return <XCircle size={16} className="text-red-600" />;
      case 'pending': return <Clock size={16} className="text-yellow-600" />;
      default: return <AlertCircle size={16} className="text-gray-600" />;
    }
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'created': return 'text-green-700';
      case 'updated': return 'text-blue-700';
      case 'deleted': return 'text-red-700';
      case 'approved': return 'text-emerald-700';
      case 'rejected': return 'text-red-700';
      case 'login': return 'text-blue-700';
      case 'logout': return 'text-gray-700';
      default: return 'text-gray-700';
    }
  };

  const exportToCSV = () => {
    const headers = [
      'Date',
      'Time',
      'Actor',
      'Role',
      'Log Type',
      'Action',
      'Target',
      'Description',
      'Result',
      'Error Message'
    ];

    const csvData = filteredLogs.map(log => {
      const date = new Date(log.created);
      return [
        date.toLocaleDateString(),
        date.toLocaleTimeString(),
        log.actor_name,
        log.actor_role,
        log.log_type,
        log.action,
        log.target_name || '-',
        log.description,
        log.result,
        log.error_message || '-'
      ];
    });

    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `activity-logs-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Activity Logs</h2>
        <p className="text-sm text-gray-600 mt-1">
          {isPartnerView ? 'Showing activity for your partner account' : 'Showing all platform activity'}
        </p>
      </div>

      {/* Filters + Export */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 flex-1">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search logs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            {/* Log Type Filter */}
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <select
                value={logTypeFilter}
                onChange={(e) => setLogTypeFilter(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
              >
                <option value="all">All Types</option>
                <option value="user_action">User Actions</option>
                <option value="advertisement_action">Advertisements</option>
                <option value="campaign_action">Campaigns</option>
                <option value="promo_request_action">Promo Requests</option>
                <option value="partner_action">Partners</option>
              </select>
            </div>
            {/* Result Filter */}
            <div>
              <select
                value={resultFilter}
                onChange={(e) => setResultFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
              >
                <option value="all">All Results</option>
                <option value="success">Success</option>
                <option value="failure">Failure</option>
                <option value="pending">Pending</option>
              </select>
            </div>
            {/* Date Range */}
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value as any)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="week">Last 7 Days</option>
                <option value="month">Last 30 Days</option>
              </select>
            </div>
          </div>
          <button
            onClick={exportToCSV}
            className="flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition md:w-auto"
          >
            <Download size={18} className="mr-2" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Logs</p>
              <p className="text-2xl font-bold text-gray-900">{filteredLogs.length}</p>
            </div>
            <FileText className="text-blue-500" size={32} />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Success</p>
              <p className="text-2xl font-bold text-green-600">
                {filteredLogs.filter(l => l.result === 'success').length}
              </p>
            </div>
            <CheckCircle className="text-green-500" size={32} />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Failures</p>
              <p className="text-2xl font-bold text-red-600">
                {filteredLogs.filter(l => l.result === 'failure').length}
              </p>
            </div>
            <XCircle className="text-red-500" size={32} />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pending</p>
              <p className="text-2xl font-bold text-yellow-600">
                {filteredLogs.filter(l => l.result === 'pending').length}
              </p>
            </div>
            <Clock className="text-yellow-500" size={32} />
          </div>
        </div>
      </div>

      {/* Logs Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date & Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Result
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredLogs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                    No activity logs found
                  </td>
                </tr>
              ) : (
                filteredLogs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex flex-col">
                        <span>{new Date(log.created).toLocaleDateString()}</span>
                        <span className="text-xs text-gray-500">
                          {new Date(log.created).toLocaleTimeString()}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-900">{log.actor_name}</span>
                        <span className="text-xs text-gray-500">{log.actor_role}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${getLogTypeColor(log.log_type)}`}>
                        {getLogTypeIcon(log.log_type)}
                        <span className="ml-1">{log.log_type.replace('_', ' ')}</span>
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`text-sm font-medium ${getActionColor(log.action)}`}>
                        {log.action}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div className="max-w-md">
                        <p className="truncate">{log.description}</p>
                        {log.target_name && (
                          <p className="text-xs text-gray-500 mt-1">Target: {log.target_name}</p>
                        )}
                        {log.error_message && (
                          <p className="text-xs text-red-600 mt-1">Error: {log.error_message}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getResultIcon(log.result)}
                        <span className="ml-2 text-sm capitalize">{log.result}</span>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
